'use client'

import clsx from 'clsx'

import { type JSX, useEffect, useState } from 'react'

import { Title } from '../design-system'
import useReducedMotion from './use-reduced-motion'

// app.js:928: the interval between steps. Production doubles it as `2800 * streamTimeScale` in a few
// other loops but this one is not time-scaled.
const STEP_MS = 2800

// Copy from index.html:225-227, unchanged.
const CARDS = [
  {
    num: '01 · Measure',
    title: 'See what candidates actually see on ChatGPT',
    body: 'Know where you show up, where you are cited, and who shows up instead.',
    arrow: '→',
  },
  {
    num: '02 · Understand',
    title: 'Understand the questions',
    body: 'Capture how candidates really ask, in their own words, on your site and inside the LLMs.',
    arrow: '→',
  },
  {
    num: '03 · Optimize',
    title: 'Optimize to be the answer',
    body: "Close the content gaps so the models recommend you the next time it's asked.",
    arrow: '↺',
  },
] as const

/**
 * The progress segment's fill state. `active` renders `--animate-progress-fill` (theme.css), a
 * one-shot `@keyframes` — not a transition — because a transition never plays on an element's first
 * rendered frame, which is exactly segment 0 on mount: with a transition it would sit permanently
 * full without ever animating in (measured in the browser). A keyframe animation DOES play on
 * insertion, and correctly restarts on every later lap too, because this class is only ever applied
 * to a segment that rendered as `complete` or `pending` (no animation) the step before — the browser
 * always sees a genuine off→on change, so no forced reflow is needed to retrigger it.
 */
function fillClassName(active: boolean, complete: boolean): string {
  if (active) return 'animate-progress-fill'
  if (complete) return 'scale-x-100'

  return 'scale-x-0'
}

/**
 * "The blind spot"'s three-step loop (`app.js:889-962`): one piece of state and one `setInterval`,
 * cleared on cleanup. No `querySelector`, no `classList`, no forced reflow — the step, the fill
 * and `aria-valuenow` all render straight from `step`. Production instead watches a second
 * IntersectionObserver to pause the loop off-screen; this trades that for one effect. Accepted
 * delta, see docs/superpowers/specs/2026-09-22-s20-825-marketing-design-system-deltas.md, section 3.6.
 *
 * Reduced motion needs no extra branch for the step itself: the effect never schedules a timer, so
 * `step` stays 0 and card 1 stays current for good — exactly production's `@media
 * (prefers-reduced-motion: reduce)` block. It DOES need one for the fill, because step 0 is reachable
 * two ways — freshly mounted (should animate in) and permanently under reduced motion (should not)
 * — so `active` also checks `reducedMotion` directly rather than inferring it from `step`.
 */
export default function MeasureLoop(): JSX.Element {
  const reducedMotion = useReducedMotion()
  const [step, setStep] = useState(0)

  useEffect(() => {
    if (reducedMotion) return undefined

    const timer = window.setInterval(() => setStep((current) => (current + 1) % CARDS.length), STEP_MS)

    return (): void => window.clearInterval(timer)
  }, [reducedMotion])

  return (
    <>
      <div
        role="progressbar"
        aria-label="Measure, understand and optimize loop progress"
        aria-valuemin={1}
        aria-valuemax={CARDS.length}
        aria-valuenow={step + 1}
        className="mt-[76px] grid grid-cols-3 gap-[7px] max-[761px]:mt-[42px] max-[761px]:gap-[5px]"
      >
        {CARDS.map((card, index) => {
          const active = !reducedMotion && index === step
          const complete = index < step || (reducedMotion && index === step)

          return (
            <span key={card.num} aria-hidden="true" className="relative h-[2px] overflow-hidden bg-fill-strong">
              <span
                className={clsx(
                  'absolute inset-0 origin-left bg-accent dark:bg-[rgba(255,255,255,0.76)]',
                  fillClassName(active, complete),
                )}
              />
            </span>
          )
        })}
      </div>
      <div className="mt-[18px] grid grid-cols-3 border-b border-border-strong max-[761px]:mt-[14px] max-[761px]:grid-cols-1">
        {CARDS.map((card, index) => {
          const current = index === step

          return (
            <div
              key={card.num}
              aria-current={current ? 'step' : undefined}
              className={clsx(
                'relative min-h-[310px] p-[32px] transition-colors duration-[220ms] ease-[ease] max-[761px]:min-h-0 max-[761px]:px-[24px] max-[761px]:pt-[24px] max-[761px]:pb-[30px]',
                index > 0 && 'border-l border-border-strong max-[761px]:border-t max-[761px]:border-l-0',
                current && 'bg-fill-subtle',
              )}
            >
              <div
                className={clsx(
                  'text-2xs leading-tight font-semibold tracking-label uppercase',
                  current ? 'text-accent' : 'text-ink-muted',
                )}
              >
                {card.num}
              </div>
              <Title as="h3" size="h3" className="mt-[58px] max-w-[330px] max-[761px]:mt-[26px]">
                {card.title}
              </Title>
              <p className="mt-[14px] max-w-[320px] text-base text-ink-muted">{card.body}</p>
              <span
                aria-hidden="true"
                className={clsx(
                  'absolute right-[24px] bottom-[22px] text-lead max-[761px]:hidden',
                  current ? 'text-accent' : 'text-ink-muted',
                )}
              >
                {card.arrow}
              </span>
            </div>
          )
        })}
      </div>
    </>
  )
}
